/* eslint-disable */

import {
  BehaviorSubject,
  combineLatest,
  firstValueFrom,
  from,
  Observable,
  of,
  ReplaySubject,
} from 'rxjs';
import { map, skipWhile } from 'rxjs/operators';

import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';

import dayjs from 'dayjs';
import ColorHash from 'color-hash/dist/esm.js';

import { IslandService } from '../setting/island/island.service';
import { IslandEntity } from '../domain/island.entity';
import { LocationService } from '../location/location.service';
import { LocationCriteria } from '../location/location.criteria';
import { LocationEntity } from '../domain/location.entity';
import { BaseEntity } from '../domain/base-entity';
import { GraphicDecorator, LayerDecorator } from './layer-decorator';
import { RecordSearchDTO } from '../record/record-search-dto';
import { BirdLabelToggleComponent } from './bird-label-toggle/bird-label-toggle.component';
import { LocationBearingEntity } from '../domain/locationbearing.entity';
import { TriangulationService } from '../triangulation/service/triangulation.service';
import { MapColorService } from './map-color.service';

import {
  splitGeometryTypes,
  symbolTypes,
  typeNames,
} from '../geometry/geojson';
import { Geometry, GeometryService } from '../geometry/geometry.service';
import { titleCase, unCamelCase } from '../common/string';

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//  Additional Classes
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export class BirdData {
  birdID: string;
  birdName: string;
  selected = true;

  constructor(birdID: string, birdName: string) {
    this.birdID = birdID;
    this.birdName = birdName;
  }
}

export class LocationPoint {
  constructor(public easting: number, public northing: number) {}
}

const baseLayerDefinition: __esri.FeatureLayerProperties = {
  geometryType: 'point',
  objectIdField: 'objectId',
  fields: [
    {
      name: 'objectId',
      alias: 'objectId',
      type: 'oid',
    },
    {
      name: 'locationName',
      alias: 'locationName',
      type: 'string',
    },
    {
      name: 'labelText',
      alias: 'labelText',
      type: 'string',
    },
    {
      name: 'mapFeatureType',
      type: 'string',
    },
    {
      name: 'infoWindowTitle',
      alias: 'infoWindowTitle',
      type: 'string',
    },
    {
      name: 'labelText',
      type: 'string',
    },
    {
      name: 'northing',
      type: 'double',
    },
    {
      name: 'easting',
      type: 'double',
    },
    {
      name: 'type',
      type: 'string',
    },
  ] as __esri.Field[],

  popupTemplate: {
    title: '{infoWindowTitle}',
    content: (f) => {
      const a = f.graphic.attributes;
      return [
        a.recordType ? `Record Type: <strong>${a.recordType}</strong>` : null,
        `Coordinates: <strong>${a.easting}, ${a.northing}</strong>`,
        a.locationName ? `Location: <strong>${a.locationName}</strong>` : null,
      ]
        .filter((l) => l !== null)
        .join('<br>');
    },
  },
};

interface GeoJsonCacheItem {
  url: string;
  layer: __esri.GeoJSONLayer;
}

type GeoJsonCache = Map<Geometry, GeoJsonCacheItem[]>;

/**
 * The eventual answer to getting labels to show on the map came from these links.
 *
 * https://community.esri.com/thread/193924-label-feature-layer-created-by-feature-collection
 * https://community.esri.com/thread/204864-how-to-label-point-feature-class-created-dynamically-in-wab
 *
 */
@Component({
  selector: 'app-esri-map',
  templateUrl: './esri-map.component.html',
  styleUrls: ['./esri-map.component.scss'],
  standalone: false,
})
export class EsriMapComponent implements OnInit, OnDestroy {
  private islandService = inject(IslandService);
  private locationService = inject(LocationService);
  private colorService = inject(MapColorService);
  private geometryService = inject(GeometryService);

  esri: typeof import('./esri-core');

  mapLoaded$ = new BehaviorSubject<boolean>(false);
  mapLoadedEvent = new EventEmitter();

  @ViewChild('islandSelector')
  islandSelector;

  @Input()
  displayMapTypeBtns = true;

  @Input()
  displayLocationTypeCheckBoxes = true;

  @Input()
  displayIslandSelector = true;

  @Input()
  positionFixed = true;

  @Input()
  set showIslandGeometry(v) {
    this.showIslandGeometry$.next(v);
  }
  showIslandGeometry$ = new BehaviorSubject<boolean>(true);

  selectedIslandName$ = new BehaviorSubject<string>(null);
  selectedIsland$ = new BehaviorSubject<IslandEntity>(null);

  _locationBearingList: LocationBearingEntity[];
  _triangulationPoint: LocationPoint;

  selectedLocation$ = new BehaviorSubject<LocationEntity>(null);

  selectedRecord$ = new BehaviorSubject<RecordSearchDTO>(null);

  @Input()
  locations: LocationEntity[] = null;

  @Input()
  set geojson(v) {
    this.geojson$.next(v);
  }
  geojson$ = new ReplaySubject<Geometry[]>(1);

  // Cache to track and dispose of Object URLs
  geojsonCache: GeoJsonCache = new Map();

  // Cache to track and dispose of Object URLs
  islandGeojsonCache: GeoJsonCache = new Map();

  @Input()
  set gpsTagData(v) {
    this.gpsTagData$.next(v);
  }
  gpsTagData$ = new ReplaySubject<any[]>(1);

  set showGpsTagTrack(v) {
    this.showGpsTagTrack$.next(v);
  }
  get showGpsTagTrack() {
    return this.showGpsTagTrack$.getValue();
  }
  showGpsTagTrack$ = new BehaviorSubject<boolean>(false);

  gpsTagLayer: __esri.FeatureLayer = null;
  gpsTagTrack: __esri.Graphic = null;

  private _birdLocations: RecordSearchDTO[] = null;

  @ViewChild(BirdLabelToggleComponent)
  birdLabelToggleComponent: BirdLabelToggleComponent;

  map: __esri.Map;
  mapView: __esri.MapView;
  layerMap = new Map<string, LayerDecorator>(); // this is a data Map for sticking stuff in, not a Map for display of GIS data
  graphicMap = new Map<string, GraphicDecorator>();

  //readonly mapServerURLTemplate = "https://services.arcgisonline.co.nz/arcgis/rest/services/{mapType}/MapServer";
  readonly mapServerURLTemplate = '/api/esri/{mapType}/MapServer';
  mapLayerGeneric: __esri.TileLayer;
  mapLayerGeotiffs: __esri.WMTSLayer;
  mapLayerImagery: __esri.TileLayer;
  spatialReference: __esri.SpatialReference;

  geoJsonLayers: __esri.GeoJSONLayer[];

  private _selectedIslandName;
  islandList: IslandEntity[] = [];

  islandList$: Observable<IslandEntity[]>;

  objectIdFactory = 1;
  // TODO populate from the async OptionsService.getOptions('LocationTypes');
  locationTypes = [
    'Brooder',
    'Helipad',
    'Hopper',
    'Incubator',
    'Nest',
    'NoraNet',
    'Pen',
    'Roost',
    'Seed tray',
    'Track and Bowl',
    'Trap',
    'Triangulation point',
    'Veg monitoring site',
  ];
  private _visibleLayers: string[] = [];

  private _colorHash = new ColorHash();

  birdList: BirdData[];

  constructor() {
    // create the layer decorators now, the mapLayer for these will come later
    this.locationTypes.forEach((locationType) => {
      this.layerMap.set(locationType, new LayerDecorator(locationType));
    });
  }

  /**
   * "compareWith" code/id comparison for selecting an Island
   */
  byId(o1: BaseEntity, o2: BaseEntity) {
    return o1 && o2 && o1.id === o2.id;
  }

  ngOnInit() {
    if (this.displayIslandSelector && !this.selectedIslandName$.getValue()) {
      this.selectedIslandName$.next('Anchor');
    }

    this.islandList$ = this.islandService
      .findAll()
      .pipe(
        map((islandList) =>
          islandList.filter(
            (island) => island.upperNorthing && island.upperEasting
          )
        )
      );

    combineLatest([this.islandList$, this.selectedIslandName$]).subscribe(
      ([islands, selectedIslandName]) => {
        if (!islands || !selectedIslandName) {
          return;
        }

        const island = islands.find((i) => i.name === selectedIslandName);

        if (island) {
          this.selectedIsland$.next(island);
        }
      }
    );

    combineLatest([this.selectedIsland$, this.mapLoaded$]).subscribe(
      async ([selectedIsland, mapLoaded]) => {
        if (!selectedIsland || !mapLoaded) {
          return;
        }

        this.onIslandSelected(selectedIsland);
      }
    );

    combineLatest([
      this.showIslandGeometry$,
      this.selectedIsland$,
      this.mapLoaded$,
    ]).subscribe(async ([showIslandGeometry, selectedIsland, mapLoaded]) => {
      if (!showIslandGeometry || !selectedIsland || !mapLoaded) {
        return;
      }

      const geometry = await firstValueFrom(
        this.geometryService.getIslandGeometries(selectedIsland.id, true)
      );

      this.updateGeoJsonLayers(
        geometry as Geometry[],
        this.islandGeojsonCache,
        {
          opacity: 0.5,
        }
      );
    });

    this.geojson$.subscribe((geojson) => {
      this.updateGeoJsonLayers(geojson, this.geojsonCache);
    });

    combineLatest([
      this.mapLoaded$,
      this.gpsTagData$,
      this.showGpsTagTrack$,
    ]).subscribe(([mapLoaded, gpsTagData, showGpsTagTrack]) => {
      if (mapLoaded) {
        if (this.gpsTagLayer) {
          this.map.remove(this.gpsTagLayer);
          this.gpsTagLayer = null;
        }
        if (this.gpsTagTrack) {
          this.mapView.graphics.remove(this.gpsTagTrack);
          this.gpsTagTrack = null;
        }
        if (gpsTagData) {
          this.updateGpsTagLayer(gpsTagData, showGpsTagTrack);
        }
      }
    });

    combineLatest([this.selectedLocation$, this.mapLoaded$]).subscribe(
      ([location, mapLoaded]) => {
        if (mapLoaded) {
          this.removeSelectedLocation();
          if (location) {
            this.addSelectedLocation();
          }
        }
      }
    );

    combineLatest([this.selectedRecord$, this.mapLoaded$]).subscribe(
      ([record, mapLoaded]) => {
        if (mapLoaded) {
          this.removeSelectedRecord();
          if (record) {
            this.addSelectedRecord();
          }
        }
      }
    );

    // Load the map once we have the initial island
    firstValueFrom(
      this.selectedIsland$.pipe(skipWhile((i) => i === null))
    ).then((i) => {
      combineLatest([this.loadLocations(), this.loadMap()]).subscribe(
        ([locations]) => {
          this.mapView.when().then(() => {
            this.mapLoaded$.next(true);
            this.mapLoadedEvent.next(null);

            // possible performance optimisation here, if we don't need to show locations then don't load them
            this.addLocations(locations);

            if (this._birdLocations) {
              this.addBirdLocations();
            }
          });
        }
      );
    });
  }

  ngOnDestroy(): void {
    if (this.map) {
      if (this.geojsonCache) {
        this.clearGeoJsonCache(this.geojsonCache);
      }
      if (this.islandGeojsonCache) {
        this.clearGeoJsonCache(this.islandGeojsonCache);
      }
    }
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  //  "Pure" Map stuff
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  loadMap(): Observable<any> {
    const selectedIsland = this.selectedIsland$.getValue();
    const initialExtent = {
      xmax: selectedIsland.upperEasting,
      xmin: selectedIsland.lowerEasting,
      ymax: selectedIsland.upperNorthing,
      ymin: selectedIsland.lowerNorthing,
      spatialReference: { wkid: 2193 },
    };

    return this.loadMapModules().pipe(
      map(() => {
        this.spatialReference = new this.esri.SpatialReference({ wkid: 2193 });

        this.map = new this.esri.Map({});

        this.mapLayerGeotiffs = new this.esri.WMTSLayer({
          url:
            this.mapServerURLTemplate.replace('{mapType}', 'LINZ/geotiffs') +
            '/WMTSCapabilities.xml',
          opacity: 0.5,
        });
        this.mapLayerGeotiffs.visible = true;
        this.map.add(this.mapLayerGeotiffs);

        this.mapLayerGeneric = new this.esri.TileLayer({
          url: this.mapServerURLTemplate.replace(
            '{mapType}',
            'Generic/newzealand'
          ),
          opacity: 0.5,
        });
        this.mapLayerGeneric.visible = false;
        this.map.add(this.mapLayerGeneric);

        this.mapLayerImagery = new this.esri.TileLayer({
          url: this.mapServerURLTemplate.replace(
            '{mapType}',
            'Imagery/newzealand'
          ),
          opacity: 0.5,
        });
        this.mapLayerImagery.visible = false;
        this.map.add(this.mapLayerImagery);

        this.mapView = new this.esri.MapView({
          map: this.map,
          container: 'mapNode',
          extent: initialExtent,
          spatialReference: this.spatialReference,
        });

        this.mapView.watch(
          'popup.viewModel.features',
          (graphics: __esri.Graphic[]) => {
            // Make a list of layer priority
            const priority = [
              'Selected Location',
              'Bird Locations',
              ...this.locationTypes,
            ]
              .map((l) => this.layerMap.get(l)?.element as __esri.Layer)
              .filter((l) => l);
            const birdLocationIndex = priority.indexOf(
              this.layerMap.get('Bird Locations')?.element as __esri.Layer
            );
            //graphics.reverse();
            graphics.sort((a, b) => {
              let aIndex = priority.indexOf(a.layer);
              let bIndex = priority.indexOf(b.layer);

              if (aIndex === -1) aIndex = Infinity;
              if (bIndex === -1) bIndex = Infinity;

              if (aIndex !== bIndex) {
                return aIndex - bIndex;
              }

              if (birdLocationIndex !== -1 && aIndex === birdLocationIndex) {
                return b.attributes.recordDate - a.attributes.recordDate;
              }

              return 0;
            });
          }
        );
      })
    );
  }

  clearGeoJsonCache(cache: GeoJsonCache) {
    for (const geojsonObject of cache.keys()) {
      const data = cache.get(geojsonObject);
      // Delete layers
      for (const layer of data) {
        this.map.remove(layer.layer);
        URL.revokeObjectURL(layer.url);
      }
      cache.delete(geojsonObject);
    }
  }

  updateGeoJsonLayers(
    geojsonLayers: Geometry[],
    cache: GeoJsonCache,
    layerProperties?: __esri.GeoJSONLayerProperties
  ) {
    if (!this.esri) {
      return;
    }

    // Remove old layers
    for (const geojsonObject of cache.keys()) {
      if (!geojsonLayers?.includes(geojsonObject)) {
        const data = cache.get(geojsonObject);
        // Delete it
        for (const item of data) {
          this.map.remove(item.layer);
          URL.revokeObjectURL(item.url);
        }
        cache.delete(geojsonObject);
      }
    }

    if (!geojsonLayers) {
      return;
    }

    this.geoJsonLayers = [];

    for (const geojsonObject of geojsonLayers) {
      if (cache.has(geojsonObject)) {
        continue;
      }

      // Split geometries into types (because of arcgis limitation
      // https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-GeoJSONLayer.html#:~:text=Each%20GeoJSONLayer%20will%20only%20accept%20one%20geometry%20type
      const types = splitGeometryTypes(geojsonObject.geoJson);

      const objectCache: GeoJsonCacheItem[] = [];

      for (const [type, features] of Object.entries(types)) {
        if (!features?.length) {
          continue;
        }

        const featureCollection = {
          type: 'FeatureCollection',
          features,
        };

        const blob = new Blob([JSON.stringify(featureCollection)], {
          type: 'application/geo+json',
        });

        const url = URL.createObjectURL(blob);

        const layer = new this.esri.GeoJSONLayer({
          ...(layerProperties || {}),
          url,
          title: `${geojsonObject.name} ${typeNames[type]}`,
          renderer: {
            type: 'unique-value',
            field: 'color',
            defaultSymbol: {
              type: symbolTypes[type],
              color: geojsonObject.color || '#ff0000',
              size: 5,
            },
          } as any,
          labelsVisible: true,
          labelingInfo: [
            {
              symbol: {
                type: 'text',
                haloColor: 'white',
                haloSize: 1,
                font: {
                  size: 8,
                  weight: 'normal',
                },
              },
              labelPlacement: 'above-along',
              labelExpressionInfo: {
                expression: '$feature.name',
              },
            },
          ],
          popupTemplate: {
            title: (f) => f.graphic.attributes.name || 'Unnamed',
            content: (f) => {
              const parts = [];
              if (f.graphic.attributes.description) {
                parts.push(f.graphic.attributes.description, '');
              }
              parts.push('Layer: ' + geojsonObject.name);
              if (geojsonObject.description) {
                parts.push('Layer Description:');
                parts.push(...geojsonObject.description.split('\n'));
              }

              return parts.join('<br>');
            },
          },
        });

        objectCache.push({
          url,
          layer,
        });

        this.map.add(layer);
      }
      cache.set(geojsonObject, objectCache);
    }

    this.reorderLayers();
  }

  loadMapModules() {
    return from(
      import('./esri-core').then((module) => {
        this.esri = module;
      })
    );
  }

  createFeatureLayer(
    layerId: string,
    features: __esri.Graphic[],
    featureOptions?: Partial<__esri.FeatureLayerProperties>
  ) {
    const renderer = new this.esri.UniqueValueRenderer({
      defaultSymbol: this.createDefaultSymbol(),
    });

    const featureProperties = {
      ...baseLayerDefinition,
      id: layerId,
      outFields: ['*'],
      labelsVisible: true,
      renderer,
      labelingInfo: [this.createLabelClass()],
      source: features,
      ...featureOptions,
      fields: [
        ...baseLayerDefinition.fields,
        ...(featureOptions?.fields || []),
      ],
    };
    //create a feature layer based on the feature collection
    const featureLayer = new this.esri.FeatureLayer(featureProperties);

    return featureLayer;
  }

  createDefaultSymbol() {
    const defaultSymbol = new this.esri.SimpleMarkerSymbol({
      style: 'circle',
      size: 10,
      outline: {
        type: 'simple-line',
        color: new this.esri.Color([0, 0, 0, 0]),
      },
      color: new this.esri.Color([255, 45, 192, 1]),
    });
    return defaultSymbol;
  }

  createLabelClass() {
    const labelClass = new this.esri.LabelClass({
      symbol: new this.esri.TextSymbol({
        color: new this.esri.Color([20, 20, 20, 0.9]),
        haloColor: new this.esri.Color([245, 255, 45, 0.8]),
        haloSize: 3,
        font: {
          size: '10pt',
          family: 'arial',
        },
      }),
      labelExpressionInfo: { expression: '$feature.labelText' },
    });

    return labelClass;
  }

  onChangeMapType(mapType: string) {
    // TODO address timing issue when changing mapType before map is loaded
    this.mapLayerGeneric.visible = false;
    this.mapLayerGeotiffs.visible = false;
    this.mapLayerImagery.visible = false;
    switch (mapType) {
      case 'NZ Topo':
        this.mapLayerGeotiffs.visible = true;
        break;
      case 'Generic':
        this.mapLayerGeneric.visible = true;
        break;
      case 'Imagery':
        this.mapLayerImagery.visible = true;
        break;
      default:
        throw new Error('Unknown mapType of ' + mapType);
    }
  }

  getLocationTypeState(locationType) {
    let state = 0;
    const layerDecorator = this.layerMap.get(locationType);
    if (layerDecorator) {
      if (layerDecorator.labelVisibility) {
        state = 2;
      } else if (layerDecorator.visibility) {
        state = 1;
      } else {
        state = 0;
      }
    }
    return state;
  }

  onLocationTypeStateChange(locationType: string, state: number) {
    const layerVisibility = state > 0;
    this.setLayerVisibility(locationType, layerVisibility);

    const labelVisibility = state === 2;
    this.layerMap.get(locationType).labelVisibility = labelVisibility;
  }

  getLayerVisibility(locationType: string) {
    return this.layerMap.get(locationType).visibility;
  }

  setLayerVisibility(locationType: string, visible: boolean) {
    const layerDecorator = this.layerMap?.get(locationType);
    if (layerDecorator) {
      layerDecorator.visibility = visible;
    }
  }

  get visibleLayers(): string[] {
    return this._visibleLayers;
  }

  @Input()
  set visibleLayers(value: string[]) {
    this._visibleLayers = value;

    // switch off all the layers first (hopefully this doesn't cause any flicker
    this.locationTypes.forEach((layerName) => {
      this.setLayerVisibility(layerName, false);
    });

    // then only switch on the ones we've been asked to make visible
    if (value && value.length) {
      value.forEach((layerName) => {
        //this.setLayerVisibility(layerName, true);
        this.onLocationTypeStateChange(layerName, 2);
      });
    }
  }

  reorderLayers() {
    let start = 10000;

    for (const locationType of this.layerMap.keys()) {
      const layerDecorator = this.layerMap.get(locationType);
      if (layerDecorator) {
        this.map.reorder(layerDecorator.element, start++);
      }
    }
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  //  Locations
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  addLocations(locations: LocationEntity[]) {
    this.locationTypes.forEach((locationType) => {
      const features = this.createLocationFeatures(locations, locationType);
      const featureLayer = this.createFeatureLayer(locationType, features, {
        popupTemplate: {
          title: 'infoWindowTitle',
          content: this.locationInfoTemplate(),
        },
        fields: [
          {
            name: 'locationType',
            type: 'string',
          },
          {
            name: 'locationId',
            type: 'string',
          },
        ],
      });

      this.map.add(featureLayer);
      this.layerMap.get(locationType).element = featureLayer;
    });
  }

  private locationInfoTemplate() {
    /* Using return element hack to avoid arcgis-html-sanitizer as it is
     * sanitizing the link to blank
     * @see https://www.esri.com/arcgis-blog/products/js-api-arcgis/mapping/using-html-with-popups-in-the-arcgis-api-for-javascript
     */
    /*return (
      'Location Type: <strong>{locationType}</strong>' +
      '<br>Coordinates: <strong>{easting}, {northing}</strong>' +
      '<br>Location: <strong>{locationName}</strong>' +
      '<br>Link: <a href="/app/record/{recordID}">Open Record</a>'
    );*/
    return (f) => {
      const a = f.graphic.attributes;

      const html = [
        a.locationType
          ? `Location Type: <strong>${a.locationType}</strong>`
          : null,
        `Coordinates: <strong>${a.easting}, ${a.northing}</strong>`,
        a.locationName ? `Location: <strong>${a.locationName}</strong>` : null,
        a.locationId
          ? `Link: <a href="/app/location/${a.locationId}" target="_blank">Open Record</a>`
          : null,
      ]
        .filter((a) => a !== null)
        .join('<br>');
      const div = document.createElement('div');
      div.innerHTML = html;
      return div;
    };
  }

  get selectedLocation() {
    return this.selectedLocation$.getValue();
  }

  @Input()
  set selectedLocation(location: LocationEntity) {
    this.selectedLocation$.next(location);
  }

  addSelectedLocation() {
    const selectedLocation = this.selectedLocation$.getValue();
    if (selectedLocation) {
      // Warning: creating a fake locationType here, but seemed the easiest way to implement things.
      const locationType = 'Selected Location';
      this.layerMap.set(locationType, new LayerDecorator(locationType));

      const feature = this.createOneLocationFeature(selectedLocation);
      const featureLayer = this.createFeatureLayer(locationType, [feature], {
        fields: [
          {
            name: 'locationType',
            type: 'string',
          },
          {
            name: 'locationId',
            type: 'string',
          },
        ],
        popupTemplate: {
          title: '{infoWindowTitle}',
          content: this.locationInfoTemplate(),
        },
      });
      this.map.add(featureLayer);
      this.layerMap.get(locationType).element = featureLayer;
      this.setLayerVisibility(locationType, true);
    }
  }

  removeSelectedLocation() {
    if (this.map) {
      // using same 'fake locationType' from addSelectedLocation
      const locationType = 'Selected Location';
      const layerDecorator = this.layerMap.get(locationType);
      // layer won't exist when selected location was never added
      if (layerDecorator && layerDecorator.element) {
        this.map.remove(layerDecorator.element);
      }
    }
  }

  get selectedRecord() {
    return this.selectedRecord$.getValue();
  }

  @Input()
  set selectedRecord(record: RecordSearchDTO) {
    this.selectedRecord$.next(record);
  }

  addSelectedRecord() {
    const selectedRecord = this.selectedRecord$.getValue();
    if (selectedRecord) {
      // Warning: creating a fake locationType here, but seemed the easiest way to implement things.
      const locationType = 'Bird Locations';
      this.layerMap.set(locationType, new LayerDecorator(locationType));

      const feature = this.createOneRecordSearchDTOFeature(
        selectedRecord,
        true,
        true,
        true
      );
      // feature will come back null if selected record contains no coordinates
      if (feature) {
        const featureLayer = this.createFeatureLayer(locationType, [feature], {
          fields: [
            {
              name: 'recordType',
              type: 'string',
            },
            {
              name: 'recordID',
              type: 'string',
            },
            {
              name: 'recordDate',
              type: 'date',
            },
          ],
          popupTemplate: {
            title: '{infoWindowTitle}',
            content: this.birdInfoTemplate(),
          },
        });
        this.map.add(featureLayer);
        this.layerMap.get(locationType).element = featureLayer;
        this.setLayerVisibility(locationType, true);
      }
    }
  }

  removeSelectedRecord() {
    if (this.map) {
      // using same 'fake locationType' from addSelectedRecord
      const locationType = 'Bird Locations';
      const layerDecorator = this.layerMap.get(locationType);
      // layer won't exist when selected record was never added
      if (layerDecorator && layerDecorator.element) {
        this.map.remove(layerDecorator.element);
      }
    }
  }

  loadLocations(): Observable<LocationEntity[]> {
    if (this.locations) {
      return of(this.locations);
    } else {
      const locationCriteria = new LocationCriteria();
      locationCriteria.pageSize = 9999;
      return this.locationService
        .search(locationCriteria)
        .pipe(map((response) => response.results));
    }
  }

  createLocationFeatures(locations: LocationEntity[], locationType: string) {
    const features = [];

    locations.forEach((location) => {
      if (location.locationType === locationType) {
        features.push(this.createOneLocationFeature(location));
      }
    });

    return features;
  }

  createOneLocationFeature(location: LocationEntity) {
    const point = new this.esri.Point({
      x: Number(location.easting),
      y: Number(location.northing),
      spatialReference: new this.esri.SpatialReference({ wkid: 2193 }),
    });
    const feature = new this.esri.Graphic({
      geometry: point,
      attributes: {
        ...location,
        labelText: location.locationName,
        infoWindowTitle: location.locationName,
        type: 'location',
      },
    });
    return feature;
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  //  Location Bearings
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  get locationBearingList(): LocationBearingEntity[] {
    return this._locationBearingList;
  }

  @Input()
  set locationBearingList(value: LocationBearingEntity[]) {
    this._locationBearingList = value;
    this.addTriangulationGraphics();
  }

  addLocationBearings(graphicsList: any[]) {
    if (this.locationBearingList) {
      this.locationBearingList.forEach((locationBearing) => {
        if (
          locationBearing &&
          locationBearing.active &&
          locationBearing.easting &&
          locationBearing.northing
        ) {
          this.addOneLocationBearing(locationBearing, graphicsList);
        }
      });
    }
  }

  addOneLocationBearing(
    locationBearing: LocationBearingEntity,
    graphicsList: any[]
  ): void {
    // Add a circle to represent the starting point
    const startOutlineSymbol = new this.esri.SimpleLineSymbol({
      color: new this.esri.Color([50, 50, 50, 0.6]),
      style: 'solid',
      width: 3,
    });

    const startSymbol = new this.esri.SimpleMarkerSymbol({
      color: new this.esri.Color([50, 50, 50, 0.25]),
      outline: startOutlineSymbol,
      style: 'circle',
    });

    const startGeometry = new this.esri.Point({
      x: locationBearing.easting,
      y: locationBearing.northing,
      spatialReference: new this.esri.SpatialReference({ wkid: 2193 }),
    });
    graphicsList.push(
      new this.esri.Graphic({
        geometry: startGeometry,
        symbol: startSymbol,
      })
    );

    // calculate a vector based on the compass bearing and how long we want the indicator lines to be
    if (
      locationBearing.compassBearing !== undefined &&
      locationBearing.compassBearing !== null
    ) {
      const vector = TriangulationService.createVectorFromCompassBearing(
        locationBearing.compassBearing,
        4000
      );

      // the indicator line is a list of x,y coordinates, add the vector to the locationBearing to get the second
      // point of the line
      const linePath = [
        [locationBearing.easting, locationBearing.northing],
        [
          locationBearing.easting + vector.x,
          locationBearing.northing + vector.y,
        ],
      ];

      // create a Graphic object with a line following each of the points in this path
      const geometry = new this.esri.Polyline({
        spatialReference: new this.esri.SpatialReference({ wkid: 2193 }),
      });
      geometry.addPath(linePath);

      const symbol = new this.esri.SimpleLineSymbol({
        color: new this.esri.Color([50, 50, 50, 0.6]),
        style: 'solid',
        width: 2,
      });

      graphicsList.push(
        new this.esri.Graphic({
          geometry,
          symbol,
        })
      );
    }
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  //  Triangulation Point
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  get triangulationPoint(): LocationPoint {
    return this._triangulationPoint;
  }

  @Input()
  set triangulationPoint(value: LocationPoint) {
    this._triangulationPoint = value;
    this.addTriangulationGraphics();
  }

  addTriangulationGraphics() {
    if (this.mapLoaded$.getValue() && this.map && this.mapView?.graphics) {
      this.addGraphics();
    } else {
      // works but slow
      /*setTimeout(() => {
        this.addTriangulationGraphics();
      }, 5000);*/

      // doesn't work
      /*this.mapLoadedEvent.subscribe(() => {
        this.addTriangulationGraphics();
      });*/

      // fails with errors
      /*this.map.on("load", () => {
        this.addTriangulationGraphics();
      });*/

      // Seems to be working, wait for our event (this.map will be true) then wait for the map's load event
      this.mapLoadedEvent.subscribe(() => {
        this.mapView.when().then(() => {
          this.addTriangulationGraphics();
        });
      });
    }
  }

  addGraphics() {
    const graphicsList = [];

    this.addLocationBearings(graphicsList);
    this.addTriangulationPoint(graphicsList);

    this.mapView.graphics.removeAll();
    graphicsList.forEach((graphic) => {
      this.mapView.graphics.add(graphic);
    });
  }

  addTriangulationPoint(graphicsList: any[]) {
    if (
      this.triangulationPoint &&
      this.triangulationPoint.easting &&
      this.triangulationPoint.northing
    ) {
      const symbol = new this.esri.SimpleMarkerSymbol({
        color: new this.esri.Color([50, 50, 50, 0.25]),
        style: 'x',
      });

      const geometry = new this.esri.Point({
        x: this.triangulationPoint.easting,
        y: this.triangulationPoint.northing,
        spatialReference: new this.esri.SpatialReference({ wkid: 2193 }),
      });

      const graphic = new this.esri.Graphic({
        geometry,
        symbol,
      });
      graphicsList.push(graphic);
    }
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  //  Birds
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  addBirdLocations() {
    this.addBirdPaths();
    this.addBirdFeatures();
  }

  private async addBirdFeatures() {
    // Warning: creating a fake locationType here, but seemed the easiest way to implement things.
    const locationType = 'Bird Locations';
    this.layerMap.set(locationType, new LayerDecorator(locationType));

    const gpsTagRecords = [];
    const otherRecords = [];

    for (const record of this.birdLocations) {
      if (record.recordType === 'GPS') {
        gpsTagRecords.push(record);
      } else {
        otherRecords.push(record);
      }
    }

    const features = [];

    // Features are created from the Record list because we want one point per Record, however if the Record doesn't
    // have location data then createOneBirdFeature() will return null, so don't add that one. Also note that because
    // the data uses a lot of fixed Locations often multiple records are at the same location, so you won't see that
    // many points on the map.
    otherRecords.forEach((birdRecord) => {
      const birdData = this.findBirdData(birdRecord.birdID);
      if (birdData.selected) {
        const feature = this.createOneBirdFeature(birdRecord);
        if (feature) {
          features.push(feature);
        }
      }
    });

    const fields = new Map<string, __esri.FieldProperties>([
      [
        'recordType',
        {
          name: 'recordType',
          type: 'string',
        },
      ],
      [
        'recordID',
        {
          name: 'recordID',
          type: 'string',
        },
      ],
      [
        'recordDate',
        {
          name: 'recordDate',
          type: 'date',
        },
      ],
    ]);

    if (gpsTagRecords) {
      if (!this.esri.projection.isLoaded()) {
        await this.esri.projection.load();
      }
      const ignoredAttributes = ['recordType', 'recordID', 'recordDate'];
      for (const record of gpsTagRecords) {
        const birdData = this.findBirdData(record.birdID);
        if (!birdData.selected) {
          continue;
        }

        const points = [];
        const baseAttributes = {
          mapFeatureType: `${record.birdName}-gps`,
          recordType: record.recordType,
          recordID: record.recordID,
          birdName: record.birdName,
        };

        for (const data of record.gpsTagData) {
          if (typeof data.satellites !== 'undefined' && !data.satellites) {
            continue;
          }

          const pointData = {
            ...baseAttributes,
            ...data,
            dateTime: data.time,
          };

          delete pointData.time;

          if (
            typeof pointData.northing === 'undefined' &&
            typeof pointData.easting === 'undefined' &&
            typeof pointData.latitude !== 'undefined' &&
            typeof pointData.latitude !== 'undefined'
          ) {
            const point: __esri.Point = this.esri.projection.project(
              new this.esri.Point({
                latitude: pointData.latitude,
                longitude: pointData.longitude,
                spatialReference: this.esri.SpatialReference.WGS84,
              }),
              this.spatialReference
            ) as any;
            pointData.easting = Math.round(point.x);
            pointData.northing = Math.round(point.y);
            delete pointData.latitude;
            delete pointData.longitude;
          }

          for (const key of Object.keys(pointData)) {
            if (!fields.has(key)) {
              fields.set(key, {
                name: key,
                type: 'string',
              });
            }
          }

          const feature = this.createOneBirdFeature(pointData);
          features.push(feature);
          points.push([feature.geometry.x, feature.geometry.y]);
        }

        if (this.birdLabelToggleComponent.displayPaths) {
          const polyline = new this.esri.Polyline({
            paths: [points],
            spatialReference: this.spatialReference,
          });
          const feature = new this.esri.Graphic({
            geometry: polyline,
            attributes: baseAttributes,
            symbol: new this.esri.SimpleLineSymbol({
              color: new this.esri.Color(this.getBirdColor(record.birdID)),
              width: 1,
              style: 'dash',
              cap: 'round',
              join: 'round',
            }),
            visible: true,
          });

          this.mapView.graphics.add(feature);
        }
      }
    }

    // Sort the feature list so all of the Latest ones are at the top of the list and so are drawn first and don't mask
    // other features
    this.sortBirdFeatures(features);

    const featureLayer = this.createFeatureLayer(locationType, features, {
      popupTemplate: {
        title: '{infoWindowTitle}',
        content: this.birdInfoTemplate(),
      },
      fields: Array.from(fields.values()),
    });

    // Symbols are created from the Bird list, because we only need a couple of types of symbols
    this.birdList.forEach((bird) => {
      this.createBirdSymbols(bird, featureLayer.renderer);
    });

    this.map.add(featureLayer);
    this.layerMap.get(locationType).element = featureLayer;
    this.setLayerVisibility(locationType, true);
  }

  private findBirdData(birdID: string): BirdData {
    return this.birdList.find((bird) => bird.birdID === birdID);
  }

  private ignoredBirdAttributes = new Set([
    'objectId',
    'locationName',
    'recordID',
    'recordType',
    'infoWindowTitle',
    'labelText',
    'mapFeatureType',
    'northing',
    'easting',
    'type',
    'recordDate',
    'birdName',
    'dateTime',
  ]);

  private birdInfoTemplate() {
    /* Using return element hack to avoid arcgis-html-sanitizer as it is
     * sanitizing the link to blank
     * @see https://www.esri.com/arcgis-blog/products/js-api-arcgis/mapping/using-html-with-popups-in-the-arcgis-api-for-javascript
     */
    /*return (
      'Record Type: <strong>{recordType}</strong>' +
      '<br>Coordinates: <strong>{easting}, {northing}</strong>' +
      '<br>Location: <strong>{locationName}</strong>' +
      '<br>Link: <a href="/app/record/{recordID}">Open Record</a>'
    );*/
    return (f) => {
      const a = f.graphic.attributes;

      const htmlParts = [
        a.recordType ? `Record Type: <strong>${a.recordType}</strong>` : null,
        `Coordinates: <strong>${a.easting}, ${a.northing}</strong>`,
        a.locationName ? `Location: <strong>${a.locationName}</strong>` : null,
      ];

      for (const key of Object.keys(a)) {
        if (a[key] === null || this.ignoredBirdAttributes.has(key)) {
          continue;
        }
        htmlParts.push(
          `${titleCase(unCamelCase(key))}: <strong>${a[key]}</strong>`
        );
      }

      if (a.recordID) {
        htmlParts.push(
          `Link: <a href="/app/record/${a.recordID}" target="_blank">Open Record</a>`
        );
      }

      const div = document.createElement('div');
      div.innerHTML = htmlParts.filter((a) => a !== null).join('<br>');
      return div;
    };
  }

  private addBirdPaths() {
    // Clear all of the paths to start with
    if (this.map && this.mapView?.graphics) {
      this.mapView.graphics.removeAll();
    } else {
      this.mapView.when().then(() => {
        this.mapView.graphics.removeAll();
      });
    }

    // Only display path if the checkbox is on AND the Bird is selected
    if (
      this.birdLabelToggleComponent &&
      this.birdLabelToggleComponent.displayPaths
    ) {
      // for each bird from the birdList
      this.birdList.forEach((bird) => {
        if (bird.selected) {
          this.addOneBirdPath(bird);
        }
      });
    }
  }

  private addOneBirdPath(bird: BirdData) {
    // find all of the records for this Bird
    const thisBirdRecords = this.birdLocations.filter(
      (record) => record.birdID === bird.birdID
    );

    // find the path of locations for this bird
    const birdPath = [];
    thisBirdRecords.forEach((record) => {
      const locationPoint = EsriMapComponent.getLocationPointFromRecord(record);
      if (locationPoint) {
        birdPath.push([locationPoint.easting, locationPoint.northing]);
      }
    });

    // create a Graphic object with a line following each of the points in this path
    const geometry = new this.esri.Polyline({
      spatialReference: new this.esri.SpatialReference({ wkid: 2193 }),
    });
    geometry.addPath(birdPath);

    const birdColor = this.getBirdColor(bird.birdID);
    const symbol = new this.esri.SimpleLineSymbol({
      color: new this.esri.Color(birdColor),
      style: 'dash',
      width: 2,
    });
    const graphic = new this.esri.Graphic({
      geometry,
      symbol,
    });

    // WARNING: this bit of ugly is needed because otherwise sometimes the add() method is being called before the
    // map is fully loaded. But not sure why because loadMap() is supposed to be synchronous
    if (this.map && this.mapView.graphics) {
      this.mapView.graphics.add(graphic);
    } else {
      this.mapView.when().then(() => {
        this.mapView.graphics.add(graphic);
      });
    }
  }

  private sortBirdFeatures(features: any[]) {
    features.sort((t1: any, t2: any) => {
      const t1Latest = t1.attributes.mapFeatureType.indexOf('Latest') > -1;
      const t2Latest = t2.attributes.mapFeatureType.indexOf('Latest') > -1;

      if (t1Latest && !t2Latest) {
        return -1;
      } else if (!t1Latest > t2Latest) {
        return 1;
      } else {
        return 0;
      }
    });
  }

  createOneRecordSearchDTOFeature(
    record: RecordSearchDTO,
    displayBirdNames: boolean,
    displayRecordDate: boolean,
    displayRecordType: boolean
  ) {
    let feature = null;
    const locationPoint = EsriMapComponent.getLocationPointFromRecord(record);
    if (record.mapFeatureType && locationPoint) {
      const objectId = this.objectIdFactory++;
      const point = new this.esri.Point({
        x: locationPoint.easting,
        y: locationPoint.northing,
        spatialReference: new this.esri.SpatialReference({ wkid: 2193 }),
      });

      let labelText = '';
      if (displayBirdNames) {
        labelText = labelText + record.birdName + ' ';
      }

      if (displayRecordDate) {
        labelText = labelText + dayjs(record.dateTime).format('D-MMM-YY') + ' ';
      }

      if (displayRecordType) {
        labelText = labelText + record.recordType;
      }

      feature = new this.esri.Graphic({
        geometry: point,
        attributes: {
          ...record,
          type: 'record',
          objectId,
          labelText,
          infoWindowTitle:
            record.birdName +
            ' ' +
            dayjs(record.dateTime).format('D-MMM-YY HH:mm'),
          recordDate: dayjs(record.dateTime).toDate().toISOString(),
          easting: locationPoint.easting,
          northing: locationPoint.northing,
          locationName: record.locationName,
          recordType: record.recordType,
          recordID: record.recordID,
          mapFeatureType: record.mapFeatureType,
        },
      });
    }

    return feature;
  }

  createOneBirdFeature(record: RecordSearchDTO) {
    return this.createOneRecordSearchDTOFeature(
      record,
      this.birdLabelToggleComponent.displayBirdNames,
      this.birdLabelToggleComponent.displayRecordDate,
      this.birdLabelToggleComponent.displayRecordType
    );
  }

  static getLocationPointFromRecord(record: RecordSearchDTO): LocationPoint {
    let easting;
    let northing = null;
    if (record.easting && record.northing) {
      easting = record.easting;
      northing = record.northing;
    } else if (record.locationEasting && record.locationNorthing) {
      easting = record.locationEasting;
      northing = record.locationNorthing;
    }

    let locationPoint = null;
    if (easting && northing) {
      locationPoint = new LocationPoint(Number(easting), Number(northing));
    }

    return locationPoint;
  }

  hasBirdData(): boolean {
    return this.birdLocations !== null;
  }

  get birdLocations(): RecordSearchDTO[] {
    return this._birdLocations;
  }

  @Input()
  set birdLocations(value: RecordSearchDTO[]) {
    if (this.mapLoaded$.getValue() && this._birdLocations) {
      this.removeBirdLocations();
    }

    this._birdLocations = value;
    this.birdList = this.getBirdListFromRecords(value);

    if (this.mapLoaded$.getValue()) {
      this.addBirdLocations();
    }
  }

  getBirdListFromRecords(recordList: RecordSearchDTO[]) {
    const birdMap = new Map<string, any>();
    recordList.forEach((record) => {
      if (!birdMap.has(record.birdID)) {
        const birdData = new BirdData(record.birdID, record.birdName);
        birdMap.set(birdData.birdID, birdData);
      }
    });

    const birdList = Array.from(birdMap.values());

    birdList.sort((t1: any, t2: any) => {
      if (t1.birdName < t2.birdName) {
        return -1;
      } else if (t1.birdName > t2.birdName) {
        return 1;
      } else {
        return 0;
      }
    });
    //todo save in the color service
    this.colorService.birdList = birdList;
    return birdList;
  }

  updateBirdLocations() {
    // This dumps the BirdLocations layer and rebuilds it. Causes a redraw "flash" but it's the simplest way of
    // doing it.
    this.removeBirdLocations();
    this.addBirdLocations();
  }

  removeBirdLocations() {
    const locationType = 'Bird Locations';
    const layerDecorator = this.layerMap.get(locationType);
    // layer won't exist if bird locations have never been added
    if (layerDecorator) {
      this.map.remove(layerDecorator.element);
    }
  }

  createBirdSymbols(bird: any, renderer: any) {
    if (bird) {
      const birdColor = new this.esri.Color(this.getBirdColor(bird.birdID));

      const normalBirdSymbol = new this.esri.SimpleMarkerSymbol({
        style: 'circle',
        size: 12,
        outline: new this.esri.SimpleLineSymbol({
          style: 'solid',
          color: new this.esri.Color([0, 0, 0, 0]),
          width: 1,
        }),
        color: birdColor,
      });

      const latestBirdSymbol = new this.esri.SimpleMarkerSymbol({
        style: 'diamond',
        size: 22,
        outline: new this.esri.SimpleLineSymbol({
          style: 'solid',
          color: new this.esri.Color([0, 0, 0, 0]),
          width: 1,
        }),
        color: birdColor,
      });

      const gpsTagBirdSymbol = new this.esri.SimpleMarkerSymbol({
        size: 6,
        color: birdColor,
        style: 'circle',
        outline: null,
      });

      renderer.field = 'mapFeatureType';
      renderer.addUniqueValueInfo({
        value: bird.birdName,
        symbol: normalBirdSymbol,
      } as any);
      renderer.addUniqueValueInfo({
        value: bird.birdName + '-Latest',
        symbol: latestBirdSymbol,
      } as any);
      renderer.addUniqueValueInfo({
        value: bird.birdName + '-gps',
        symbol: gpsTagBirdSymbol,
      });
    }
  }

  private getBirdColor(birdID: string) {
    let colorP = 0;
    const colorRamp = this.colorService.colorRamp;
    for (colorP = 0; colorP < colorRamp.length; colorP++) {
      if (birdID === this.birdList[colorP].birdID) {
        break;
      }
    }
    if (colorP < colorRamp.length) {
      return colorRamp[colorP];
    } else {
      const color = this._colorHash.rgb(birdID);
      color.push(1);
      return color;
    }
  }

  onBirdSelectionChanges() {
    this.updateBirdLocations();
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  //  Islands
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  selectIsland(island: IslandEntity) {
    this.selectedIslandName$.next(island.name);
  }

  onIslandSelected(island: IslandEntity) {
    const box = {
      xmax: island.upperEasting,
      xmin: island.lowerEasting,
      ymax: island.upperNorthing,
      ymin: island.lowerNorthing,
      spatialReference: { wkid: 2193 },
    };

    const nextExtent = new this.esri.Extent(box);
    this.mapView.goTo(nextExtent);
  }

  get selectedIslandName() {
    return this.selectedIslandName$.getValue();
  }

  @Input()
  set selectedIslandName(value) {
    this.selectedIslandName$.next(value);
  }

  ///////////////////////////////////////
  // GPS Tag Data
  ///////////////////////////////////////
  async updateGpsTagLayer(gpsTagData, showGpsTagTrack: boolean) {
    const attributes = new Map<string, __esri.FieldProperties>([
      [
        'time',
        {
          name: 'time',
          type: 'date',
        },
      ],
      [
        'northing',
        {
          name: 'northing',
          type: 'double',
        },
      ],
      [
        'easting',
        {
          name: 'easting',
          type: 'double',
        },
      ],
      [
        'type',
        {
          name: 'type',
          type: 'string',
        },
      ],
    ]);

    if (gpsTagData && gpsTagData.length) {
      let data = gpsTagData;
      const features = [];
      const points = [];

      for (let i = 0; i < data.length; i++) {
        let record = data[i];
        let point: __esri.Point;
        if (
          typeof record.satellites !== 'undefined' &&
          record.satellites === 0
        ) {
          continue;
        }
        if (record.latitude && record.longitude) {
          // Convert to easting and northing
          if (!this.esri.projection.isLoaded()) {
            await this.esri.projection.load();
          }

          point = this.esri.projection.project(
            new this.esri.Point({
              latitude: record.latitude,
              longitude: record.longitude,
              spatialReference: this.esri.SpatialReference.WGS84,
            }),
            this.spatialReference
          ) as any;
          if (data === gpsTagData) {
            data = [...gpsTagData];
          }
          record = data[i] = {
            ...data[i],
            easting: Math.round(point.x),
            northing: Math.round(point.y),
          };
        } else if (record.easting && record.northing) {
          point = new this.esri.Point({
            x: record.easting,
            y: record.northing,
            spatialReference: this.spatialReference,
          });
        }

        if (showGpsTagTrack) {
          points.push([point.x, point.y]);
        }

        // Ensure attributes are in attributes map
        for (const key of Object.keys(record)) {
          if (!attributes.has(key)) {
            attributes.set(key, {
              name: key,
              type: 'string',
            });
          }
        }

        const feature = new this.esri.Graphic({
          geometry: point,
          attributes: {
            type: 'gpsTag',
            infoWindowTitle: `${dayjs(record.time).format(
              'DD/MM/YYYY HH:mm:ss'
            )}`,
            ...record,
          },
        });

        features.push(feature);
      }

      if (showGpsTagTrack) {
        const track = new this.esri.Polyline({
          paths: [points],
          spatialReference: this.spatialReference,
        });
        this.gpsTagTrack = new this.esri.Graphic({
          geometry: track,
          symbol: new this.esri.SimpleLineSymbol({
            color: 'red',
            width: 1,
            style: 'dash',
            cap: 'round',
            join: 'round',
          }),
          attributes: {
            infoWindowTitle: 'GPS Tag track',
          },
          visible: true,
        });
        this.mapView.graphics.add(this.gpsTagTrack);
      }

      const ignoredAttributes = [
        'time',
        'latitude',
        'longitude',
        'northing',
        'easting',
        'infoWindowTitle',
        'type',
        'objectId',
      ];

      this.gpsTagLayer = this.createFeatureLayer('GPS Tag Data', features, {
        fields: Array.from(attributes.values()),
        renderer: new this.esri.SimpleRenderer({
          symbol: new this.esri.SimpleMarkerSymbol({
            size: 6,
            color: 'red', // this.getBirdColor
            style: 'circle',
            outline: null,
          }),
        }),
        popupTemplate: {
          title: '{infoWindowTitle}',
          content: (f) => {
            const a = f.graphic.attributes;
            return [
              `Time: <strong>${dayjs(a.time).format(
                'DD/MM/YYYY HH:mm:ss'
              )}</strong>`,
              `Coordinates: <strong>${a.easting}, ${a.northing}</strong>`,
              ...Object.entries(f.graphic.attributes)
                .filter(
                  ([k, v]) => !ignoredAttributes.includes(k) && v !== null
                )
                .map(
                  ([k, v]) =>
                    `${titleCase(unCamelCase(k))}: <strong>${v}</strong>`
                ),
            ]
              .filter((l) => l !== null)
              .join('<br>');
          },
        },
        visible: true,
      });
      this.map.add(this.gpsTagLayer);
      /*this.layerMap.get(locationType).element = this.gpsTagLayer;
      this.setLayerVisibility(locationType, true);*/
    }
  }
}
